import { ContextMenuDropdown, InputGroup, Tooltip } from '@payaca/ambrosia';
import { isValidEmailAddress } from '@payaca/types/emailSchema';
import UntitledIcon from '@payaca/untitled-icons';
import { type Contact } from './contacts';
import { type DraftSendEmailState } from './types';

const getSubmitLabel = (params: {
  state: DraftSendEmailState;
  isSubmitting: boolean;
}): string => {
  const { state, isSubmitting } = params;
  switch (state) {
    case 'send':
      return isSubmitting ? 'Sending…' : 'Confirm & send';
    case 'resend':
      return isSubmitting ? 'Resending…' : 'Resend';
    case 'bounced':
      return isSubmitting ? 'Resending…' : 'Confirm & resend';
  }
};

const getRecipientDisabledReason = (params: {
  recipient: Contact | null;
  entityLabel: string;
}): string | null => {
  const { recipient, entityLabel } = params;
  if (!recipient) {
    return `Select a contact to send the ${entityLabel}.`;
  }
  if (!recipient.email) {
    return 'The selected contact has no email address. Add one before sending.';
  }
  if (!isValidEmailAddress(recipient.email)) {
    return `The selected contact's email address (${recipient.email}) isn't valid. Update it on the contact before sending.`;
  }
  return null;
};

type DraftSendEmailSubmitButtonProps = {
  state: DraftSendEmailState;
  /** Drives entity-specific copy in the gating reasons. */
  entityLabel: 'estimate' | 'proposal';
  recipient: Contact | null;
  isLoading: boolean;
  isSubmitting: boolean;
  isSuppressed?: boolean;
  /**
   * Parent-supplied gating (e.g. sendability, client portal subdomain).
   * Takes precedence over the button's internal recipient checks; shown in
   * the submit button's tooltip.
   */
  submitDisabledReason?: string | null;
  additionalActions?: {
    label: string;
    /** Runs directly, bypassing send-email validation (e.g. "Mark as sent"). */
    onClick: () => void;
    disabled?: boolean;
    /** Shown as supporting text under the label when `disabled`. */
    disabledReasons?: string[];
  }[];
  /**
   * Accessible name for the `additionalActions` dropdown. Name it after what it
   * governs — the surface may carry another menu, and two dropdowns both
   * falling back to "More options" are indistinguishable to a screen reader
   * and to a role-based locator.
   */
  additionalActionsLabel?: string;
};

/**
 * Submit control for the draft-send email form. Renders inside the form's
 * `<form>` so the primary button submits it. Additional actions are plain
 * callbacks that run directly rather than submitting, so they aren't gated by
 * the send-email validation (e.g. "Mark as sent" needs no recipient email).
 */
const DraftSendEmailSubmitButton = (props: DraftSendEmailSubmitButtonProps) => {
  const {
    state,
    entityLabel,
    recipient,
    isLoading,
    isSubmitting,
    isSuppressed = false,
    submitDisabledReason,
    additionalActions,
    additionalActionsLabel,
  } = props;

  // In `bounced` state the recipient email's validity is enforced by the
  // visible field's schema. Otherwise the email is the pinned Contact's and
  // can't be edited here, so gate the button on it directly and surface the
  // reason via the tooltip rather than silently blocking submission on a
  // hidden field.
  let internalDisabledReason: string | null;
  if (state === 'bounced') {
    internalDisabledReason = recipient
      ? null
      : `Select a contact to send the ${entityLabel}.`;
  } else {
    internalDisabledReason = getRecipientDisabledReason({
      recipient,
      entityLabel,
    });
  }
  const disabledReason = submitDisabledReason ?? internalDisabledReason;

  const submitDisabled =
    isLoading || isSubmitting || isSuppressed || !!disabledReason;

  return (
    <Tooltip tooltipContent={disabledReason} enabled={!!disabledReason}>
      <InputGroup sizeVariant="sm">
        {/*
          Gate with `aria-disabled` rather than the native `disabled` attribute
          so the button stays focusable: keyboard users can reach it and the
          tooltip reason opens on focus. Block the submit (click and implicit
          Enter) here instead.
        */}
        <InputGroup.Button
          type="submit"
          aria-disabled={submitDisabled}
          onClick={(event) => {
            if (submitDisabled) event.preventDefault();
          }}
        >
          <UntitledIcon name="send-01.3" className="size-4" />
          {getSubmitLabel({ state, isSubmitting })}
        </InputGroup.Button>

        {!!additionalActions?.length && (
          <ContextMenuDropdown
            className="ml-px"
            as={InputGroup.Button}
            label={additionalActionsLabel}
            menuItems={additionalActions.map((i) => ({
              ...i,
              onClick: () => i.onClick(),
            }))}
          />
        )}
      </InputGroup>
    </Tooltip>
  );
};

export default DraftSendEmailSubmitButton;
